import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Database, Download, Upload, AlertTriangle } from "lucide-react";
import { GlassCard, PillButton, SectionTitle } from "./_shared";

export default function AdminBackup() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/backup/stats");
      setStats(data);
    } catch {
      /* noop */
    }
  };
  useEffect(() => {
    load();
  }, []);

  const exportBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prosperr-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup téléchargé");
    } catch { toast.error("Erreur export backup"); }
    finally { setBusy(false); }
  };

  const handleRestore = async (file) => {
    if (!file) return;
    if (!window.confirm(`⚠️ Restaurer depuis "${file.name}" ? Les données actuelles seront remplacées (sauf le super-admin).`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.restored_docs} documents restaurés`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur restauration");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <SectionTitle icon={Database}>Sauvegarde & Restauration</SectionTitle>

      <GlassCard className="p-5 bg-gradient-to-br from-emerald-500/10 via-transparent to-emerald-500/10">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-500/100/20 border border-emerald-500 grid place-items-center shrink-0">
            <Database className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="flex-1">
            <div className="text-[var(--text)] font-semibold">Snapshot complet de la plateforme</div>
            <div className="text-[11px] text-zinc-400 mt-1">
              {stats ? <>Total : <span className="text-[var(--text)] font-bold tabular-nums">{stats.total}</span> documents</> : "—"}
            </div>
          </div>
        </div>
        <PillButton
          variant="primary"
          onClick={exportBackup}
          disabled={busy}
          testId="backup-export"
          className="mt-4 w-full h-11"
        >
          <Download className="w-3.5 h-3.5 inline mr-1" />{busy ? "En cours…" : "Télécharger le backup JSON"}
        </PillButton>
      </GlassCard>

      {stats && (
        <GlassCard className="p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-3">Contenu</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {Object.entries(stats.collections).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="text-zinc-300 truncate">{k}</span>
                <span className="text-[var(--text)] font-bold tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      <GlassCard className="p-5 border-rose-500">
        <div className="flex items-center gap-2 text-rose-300 font-semibold text-sm mb-2">
          <AlertTriangle className="w-4 h-4" />
          Zone dangereuse — Restauration
        </div>
        <div className="text-[11px] text-zinc-400 leading-relaxed">
          Uploadez un fichier backup JSON. Les collections actuelles seront écrasées. Le super-admin
          (<code className="text-[var(--text)]">admin@prosperr.xyz</code>) est préservé.
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          onChange={(e) => handleRestore(e.target.files?.[0])}
          className="hidden"
          data-testid="backup-restore-input"
        />
        <PillButton
          variant="danger"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          testId="backup-restore-btn"
          className="mt-3 w-full h-10"
        >
          <Upload className="w-3.5 h-3.5 inline mr-1" />{busy ? "En cours…" : "Restaurer depuis un fichier"}
        </PillButton>
      </GlassCard>
    </div>
  );
}
